import { Reveal, Stagger, StaggerItem } from "@/components/anim/Reveal";
import CTASection from "@/components/site/CTASection";
import { motion, useMotionValue, useSpring, useTransform } from "framer-motion";
import { useEffect, useRef } from "react";
import { Target, HandshakeIcon, ShieldCheck, Sparkles } from "lucide-react";

const AnimatedCounter = ({ to, suffix = "", decimals = 0 }) => {
  const ref = useRef(null);
  const value = useMotionValue(0);
  const spring = useSpring(value, { duration: 1800, bounce: 0 });
  const display = useTransform(spring, (l) => (decimals > 0 ? l.toFixed(decimals) : Math.round(l).toString()));
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver(([e]) => { if (e.isIntersecting) { value.set(to); io.disconnect(); } }, { threshold: 0.4 });
    io.observe(el);
    return () => io.disconnect();
  }, [to, value]);
  return (
    <span ref={ref} className="inline-flex items-baseline">
      <motion.span>{display}</motion.span>
      <span>{suffix}</span>
    </span>
  );
};

const About = () => {
  const values = [
    { icon: Target, title: "Outcome-Obsessed", desc: "Business KPIs first, technology second." },
    { icon: HandshakeIcon, title: "Long-Term Partner", desc: "12+ years serving India's growing enterprises." },
    { icon: ShieldCheck, title: "Enterprise Grade", desc: "ISO-aligned processes, carrier-grade SLAs." },
    { icon: Sparkles, title: "Innovation Forward", desc: "AI, SASE and cloud-native by default." },
  ];

  return (
    <div data-testid="page-about">
      <section className="relative pt-24 pb-16 md:pt-32 md:pb-24">
        <div className="fs-grid-bg" />
        <div className="fs-container relative">
          <Reveal><div className="fs-eyebrow">About Four Square</div></Reveal>
          <Reveal delay={0.1}>
            <h1 className="fs-heading text-4xl md:text-6xl mt-5 max-w-4xl">
              Building the <span className="fs-gradient-text">nervous system</span> of modern Indian enterprises.
            </h1>
          </Reveal>
          <Reveal delay={0.2}>
            <p className="mt-6 text-[color:var(--text-dim)] max-w-2xl leading-relaxed">
              Headquartered in Chennai, Four Square is a full-stack enterprise
              technology partner spanning Microsoft, connectivity, contact centre
              and cyber security. We help IT leaders design, deliver and operate
              infrastructure that outlives their tenure.
            </p>
          </Reveal>

          <Stagger className="mt-14 grid grid-cols-2 md:grid-cols-4 gap-4" stagger={0.08}>
            {[
              { label: "Enterprises Served", value: 700, suffix: "+" },
              { label: "Solution Lines", value: 16, suffix: "" },
              { label: "Cities Covered", value: 60, suffix: "+" },
              { label: "NOC Support", value: 24, suffix: "/7" },
            ].map((s) => (
              <StaggerItem key={s.label}>
                <div className="fs-card p-6">
                  <div className="text-3xl md:text-4xl font-semibold text-[color:var(--cyan-bright)]">
                    <AnimatedCounter to={s.value} suffix={s.suffix} />
                  </div>
                  <div className="mt-2 text-xs tracking-[0.22em] uppercase text-[color:var(--text-muted)]">{s.label}</div>
                </div>
              </StaggerItem>
            ))}
          </Stagger>
        </div>
      </section>

      <section className="fs-section relative">
        <div className="fs-container">
          <Reveal><div className="fs-eyebrow">Our Mission</div></Reveal>
          <Reveal delay={0.1}>
            <h2 className="fs-heading mt-4 text-3xl md:text-5xl max-w-4xl">
              To make world-class enterprise technology{" "}
              <span className="fs-gradient-text">accessible, accountable and outcome-driven</span>{" "}
              for every Indian business.
            </h2>
          </Reveal>

          <Stagger className="mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4" stagger={0.07}>
            {values.map((v) => {
              const Icon = v.icon;
              return (
                <StaggerItem key={v.title}>
                  <div className="fs-card p-6 h-full">
                    <div className="fs-icon-tile"><Icon size={20} /></div>
                    <h3 className="mt-5 text-lg font-semibold">{v.title}</h3>
                    <p className="mt-2 text-sm text-[color:var(--text-dim)] leading-relaxed">{v.desc}</p>
                  </div>
                </StaggerItem>
              );
            })}
          </Stagger>
        </div>
      </section>

      <CTASection />
    </div>
  );
};

export default About;
